import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, Plug, CheckCircle2, LinkIcon, AlertTriangle, Building2, Unplug } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadStatus = async () => {
    const { data } = await api.get("/procore/status");
    setStatus(data);
    return data;
  };

  useEffect(() => {
    loadStatus().finally(() => setLoading(false));
    const p = params.get("procore");
    if (p === "connected") toast.success("Connected to Procore. Now select your company.");
    if (p === "error") toast.error("Procore connection failed. Check your keys and try again.");
    if (p) { params.delete("procore"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  // When connected but no company chosen, load the company list for the dropdown.
  useEffect(() => {
    if (status?.connected && !status?.company_id && companies === null) {
      api.get("/procore/companies")
        .then((r) => setCompanies(r.data))
        .catch((e) => { setCompanies([]); toast.error(apiError(e.response?.data?.detail)); });
    }
  }, [status, companies]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/procore/authorize-url");
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const saveCompany = async () => {
    const c = (companies || []).find((x) => String(x.id) === String(selected));
    if (!c) return;
    setBusy(true);
    try {
      await api.post("/procore/company", { company_id: c.id, company_name: c.name });
      toast.success(`Company set to ${c.name}`);
      setCompanies(null);
      await loadStatus();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/procore/disconnect");
      toast.success("Disconnected from Procore");
      setCompanies(null);
      await loadStatus();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;

  const connectedWithCompany = status?.connected && status?.company_id;

  return (
    <div className="max-w-2xl space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-head text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">Integrations and connected services.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid="procore-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded bg-[#0B192C] flex items-center justify-center">
            <Plug className="w-5 h-5 text-[#E8791A]" />
          </div>
          <div>
            <h2 className="font-head text-lg font-semibold text-slate-900">Procore</h2>
            <p className="text-xs text-slate-500">Sync budgets, cost codes and actuals from Procore.</p>
          </div>
          {connectedWithCompany && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Connected
            </span>
          )}
        </div>

        {/* Not configured */}
        {!status?.configured && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2" data-testid="procore-not-configured">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Procore keys are not configured yet. Add <code className="font-mono">PROCORE_CLIENT_ID</code>,
              <code className="font-mono"> PROCORE_CLIENT_SECRET</code>, <code className="font-mono">PROCORE_REDIRECT_URI</code>,
              <code className="font-mono"> PROCORE_AUTH_URL</code> and <code className="font-mono">PROCORE_API_URL</code> in the
              Secrets panel, then reload this page.
            </div>
          </div>
        )}

        {/* Configured, not connected */}
        {status?.configured && !status?.connected && (
          <div data-testid="procore-connect">
            <p className="text-sm text-slate-600 mb-3">
              Authorize this app to access your Procore account using OAuth 2.0.
            </p>
            <Button onClick={connect} disabled={busy} className="bg-[#E8791A] hover:bg-[#D66A0F] text-white" data-testid="connect-procore-button">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
              Connect to Procore
            </Button>
            {status?.redirect_uri && (
              <p className="text-[11px] text-slate-400 mt-2">Redirect URI: <span className="font-mono">{status.redirect_uri}</span></p>
            )}
          </div>
        )}

        {/* Connected, choose company */}
        {status?.connected && !status?.company_id && (
          <div data-testid="procore-choose-company">
            <p className="text-sm text-slate-600 mb-3">Connected. Select the company to use:</p>
            {companies === null ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading companies…</div>
            ) : companies.length === 0 ? (
              <p className="text-sm text-slate-500">No companies returned by Procore.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger className="h-9 w-72" data-testid="company-select"><SelectValue placeholder="Select a company" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)} data-testid={`company-option-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={saveCompany} disabled={busy || !selected} className="bg-[#0B192C]" data-testid="save-company-button">
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
                </Button>
                <Button variant="outline" onClick={disconnect} disabled={busy} className="hover:bg-red-50 hover:text-red-700">
                  <Unplug className="w-4 h-4 mr-1" />Disconnect
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Fully connected */}
        {connectedWithCompany && (
          <div className="flex flex-wrap items-center gap-3" data-testid="procore-connected">
            <div className="flex items-center gap-2 text-sm text-slate-800">
              <Building2 className="w-4 h-4 text-[#E8791A]" />
              Connected to Procore as <span className="font-semibold" data-testid="procore-company-name">{status.company_name}</span>
            </div>
            <Button variant="outline" onClick={disconnect} disabled={busy} className="ml-auto hover:bg-red-50 hover:text-red-700" data-testid="disconnect-procore-button">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unplug className="w-4 h-4 mr-1" />}Disconnect
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
